import { useMembersInfinite } from '@features/members/hooks/useMembers';
import Autocomplete from '@features/ui-form/components/Autocomplete';
import FieldWrapper from '@features/ui-form/components/FieldWrapper';
import { useAutocompleteInfinite } from '@features/ui-form/hooks/useAutocompleteInfinite';
import { useAutocompleteSearch } from '@features/ui-form/hooks/useAutocompleteSearch';
import { useTheme } from '@mui/joy';
import { useTranslation } from 'next-i18next';

const MemberMultiAutocomplete = Autocomplete(true, false);

export interface EntityFilterInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  hideError?: boolean;
  required?: boolean;
}

export const EntityFilterInput = ({ value, onChange, hideError = false, required = false }: EntityFilterInputProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();

  const { inputValue, searchValue, onSearch } = useAutocompleteSearch();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useMembersInfinite({
    filters: { name: searchValue },
  });
  const { isOpen, handleScroll, handleOpen, handleClose } = useAutocompleteInfinite({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const memberOptions =
    data?.pages.flatMap(page =>
      page.items.map(member => ({
        value: member.id,
        label: member.name,
        labelString: member.name,
      })),
    ) ?? [];

  const handleChange = (event: { target: { value: string } }) => {
    const raw = event.target.value;

    if (!raw) {
      onChange([]);
      return;
    }

    try {
      const parsed = JSON.parse(raw) as string[];
      if (Array.isArray(parsed)) {
        onChange(parsed);
      } else {
        onChange([]);
      }
    } catch {
      onChange([]);
    }
  };

  return (
    <FieldWrapper name="entity" label={t('sst.filter.entity')} hideError={hideError} required={required}>
      {MemberMultiAutocomplete({
        value: JSON.stringify(value ?? []),
        onChange: handleChange,
        options: memberOptions,
        placeholder: t('sst.filter.entity.placeholder'),
        iconColor: theme.palette.grey4,
        onListboxScroll: handleScroll,
        loading: isFetchingNextPage,
        open: isOpen,
        onOpen: handleOpen,
        onClose: handleClose,
        enableFetchOnScroll: true,
        onSearch,
        inputValue,
      })}
    </FieldWrapper>
  );
};
